import config from "@/config";
import { isPlainObject } from "lodash-es";
import { User } from "oidc-client-ts";
import {
  getOidcUserStorageKey,
  oidcUserMemoryStorage,
} from "@/provider/AuthProvider/oidcUserStore";

function isPlainObjectType(value: unknown): value is Record<string, unknown> {
  return isPlainObject(value);
}

let cachedUserInfoEndpoint: string | undefined;

async function getUserInfoEndpoint(signal: AbortSignal) {
  if (cachedUserInfoEndpoint) return cachedUserInfoEndpoint;

  const response = await fetch(
    `${config.oidc.authority}/.well-known/openid-configuration`,
    { signal }
  );
  if (!response.ok) throw new Error(`Discovery failed: ${response.status}`);

  const data: unknown = await response.json();
  if (!isPlainObjectType(data) || typeof data.userinfo_endpoint !== "string") {
    throw new Error("Invalid discovery document");
  }

  cachedUserInfoEndpoint = data.userinfo_endpoint;
  return cachedUserInfoEndpoint;
}

export async function fetchProfileLocale(
  accessToken: string,
  signal: AbortSignal
) {
  const endpoint = await getUserInfoEndpoint(signal);

  const response = await fetch(endpoint, {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal,
  });
  if (!response.ok) return undefined;

  const data: unknown = await response.json();
  if (!isPlainObjectType(data) || typeof data.locale !== "string") {
    return undefined;
  }

  return data.locale;
}

export function updateStoredProfileLocale(locale: string) {
  const stored = oidcUserMemoryStorage.getItem(getOidcUserStorageKey());
  if (stored === null) return;

  try {
    const user = User.fromStorageString(stored);
    user.profile = { ...user.profile, locale };
    oidcUserMemoryStorage.setItem(
      getOidcUserStorageKey(),
      user.toStorageString()
    );
  } catch {
    // Non-critical; locale will be re-fetched on next visibility change
  }
}
